import { supabase } from '@/integrations/supabase/client';
import { CoachingIntervention, CoachingScript } from '@/types';
import { handleDatabaseError, handleAuthError } from '@/lib/errorHandler';

export const coachingService = {
  /**
   * Buscar scripts de coaching ativos
   */
  async getActiveScripts(): Promise<CoachingScript[]> {
    try {
      const { data, error } = await supabase
        .from('coaching_scripts')
        .select('*')
        .eq('is_active', true)
        .order('priority', { ascending: true });

      if (error) throw error;

      return (data || []).map(d => ({
        id: d.id,
        scriptKey: d.script_key,
        interventionType: d.intervention_type,
        promptText: d.prompt_text,
        followUpQuestions: d.follow_up_questions as string[],
        isActive: d.is_active,
        priority: d.priority,
        createdAt: d.created_at
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  },

  /**
   * Buscar script específico por chave
   */
  async getScriptByKey(scriptKey: string): Promise<CoachingScript | null> {
    try {
      const { data, error } = await supabase
        .from('coaching_scripts')
        .select('*')
        .eq('script_key', scriptKey)
        .eq('is_active', true)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        scriptKey: data.script_key,
        interventionType: data.intervention_type,
        promptText: data.prompt_text,
        followUpQuestions: data.follow_up_questions as string[],
        isActive: data.is_active,
        priority: data.priority,
        createdAt: data.created_at
      };
    } catch (error) {
      return null;
    }
  },

  /**
   * Criar intervenção de coaching
   */
  async createIntervention(
    interventionType: CoachingIntervention['interventionType'],
    context: CoachingIntervention['context'],
    scriptTemplate: string
  ): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        handleAuthError(new Error('Usuário não autenticado'));
        throw new Error('Usuário não autenticado');
      }

      const { data, error } = await supabase
        .from('coaching_interventions')
        .insert({
          user_id: user.id,
          intervention_type: interventionType,
          context: context,
          script_template: scriptTemplate,
          dismissed: false
        })
        .select()
        .single();

      if (error) throw error;
      return data.id;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Buscar intervenções ativas (não dispensadas)
   */
  async getActiveInterventions(): Promise<CoachingIntervention[]> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('coaching_interventions')
        .select('*')
        .eq('user_id', user.id)
        .eq('dismissed', false)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;

      return (data || []).map(d => ({
        id: d.id,
        userId: d.user_id,
        interventionType: d.intervention_type as CoachingIntervention['interventionType'],
        context: d.context as CoachingIntervention['context'],
        scriptTemplate: d.script_template,
        userResponse: d.user_response,
        dismissed: d.dismissed,
        createdAt: d.created_at
      }));
    } catch (error) {
      handleDatabaseError(error);
      return [];
    }
  },

  /**
   * Dispensar intervenção
   */
  async dismissIntervention(interventionId: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('coaching_interventions')
        .update({ dismissed: true })
        .eq('id', interventionId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Registrar resposta do usuário
   */
  async logUserResponse(interventionId: string, response: string): Promise<void> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { error } = await supabase
        .from('coaching_interventions')
        .update({ user_response: response })
        .eq('id', interventionId)
        .eq('user_id', user.id);

      if (error) throw error;
    } catch (error) {
      handleDatabaseError(error);
      throw error;
    }
  },

  /**
   * Processar template de prompt com contexto
   */
  processPromptTemplate(template: string, context: Record<string, any>): string {
    let processed = template;
    
    Object.entries(context).forEach(([key, value]) => {
      const placeholder = `{${key}}`;
      processed = processed.replace(new RegExp(placeholder, 'g'), String(value));
    });

    return processed;
  }
};
